import React, { Component, type ComponentType, type ReactNode } from 'react';
// eslint-disable-next-line jira/restricted/styled-components, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { ThemeProvider } from 'styled-components';
import '@atlaskit/css-reset';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { ErrorBoundaryFlag as ErrorBoundary } from '@atlassian/jira-error-boundary-flag-renderer';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls';
import { ProjectContextProvider } from '@atlassian/jira-providers-project-context/src';
import type { ProjectContextData } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import {
	type ApplicationEdition,
	getEditionForProject,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { ProjectSubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/project-sub-product-updater/index.tsx';
import AppContainer from '@atlassian/jira-spa-apps-project-settings-common/src/components/app-container';
import { ProjectSettingsController } from '@atlassian/jira-spa-apps-project-settings-common/src/components/routing-filter';
import { SharedSettingsDocumentTitle } from '@atlassian/jira-spa-apps-project-settings-common/src/shared-document-title';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import type { Props as PeoplePageSpaProps } from '@atlassian/jira-users-and-roles/src';
import PeopleApp from '@atlassian/jira-users-and-roles/src/main.tsx';
import { Link, type RouteContext as Props, withRouter } from '@atlassian/react-resource-router';

const METRICS_PAGE_ID = 'project-settings-apps.people-page';

// eslint-disable-next-line jira/react/no-class-components
export class PeoplePage extends Component<Props> {
	static renderApp(
		{
			tenantContext,
		}: {
			tenantContext: TenantContext;
		},
		component: ComponentType<PeoplePageSpaProps>,
		projectContext: ProjectContextData,
	) {
		const App: ComponentType<PeoplePageSpaProps> | null | undefined = component;
		const projectContextData = projectContext.data;

		if (!App || !projectContextData || !tenantContext) {
			return <Spinner />;
		}

		const renderDetailsLink = (content: ReactNode) => (
			<Link
				href={`${getNextgenSettingsUrl(
					projectContextData.projectType,
					projectContextData.projectKey,
				)}/details`}
			>
				<span key="spa-nested-details-link">{content}</span>
			</Link>
		);

		const isAdmin = Boolean(projectContextData.isProjectAdmin);

		const appEdition: ApplicationEdition = getEditionForProject(
			projectContextData.projectType,
			tenantContext.appEditions,
		);

		return (
			projectContext.data && (
				<>
					<AppContainer baseUrl={tenantContext.baseUrl} projectKey={projectContextData.projectKey}>
						<App
							baseUrl={tenantContext.baseUrl}
							locale={tenantContext.locale}
							isAdmin={isAdmin}
							isSiteAdmin={tenantContext.isSiteAdmin}
							isProjectSimplified={projectContextData.simplified}
							accountId={tenantContext.atlassianAccountId}
							projectId={+projectContextData.projectId}
							projectKey={projectContextData.projectKey}
							projectType={projectContextData.projectType}
							showBreadcrumbs={false}
							appEdition={appEdition}
							projectName={projectContextData.projectName}
							renderDetailsLink={renderDetailsLink}
							cloudName={tenantContext.cloudName}
						/>
					</AppContainer>
					<SpaStatePageReady />
				</>
			)
		);
	}

	renderContents() {
		const { projectKey } = this.props.match.params;

		return (
			<ErrorBoundary id={METRICS_PAGE_ID}>
				<TenantContextSubscriber>
					{({ tenantContext }) => (
						<ProjectContextProvider>
							{(projectContext) => (
								<>
									{projectContext.data && (
										<SharedSettingsDocumentTitle
											projectName={projectContext.data.projectName}
											section="people"
											projectType={projectContext.data.projectType}
										/>
									)}
									{projectKey && <ProjectSubProductUpdater />}
									<ThemeProvider theme={{}}>
										{PeoplePage.renderApp({ tenantContext }, PeopleApp, projectContext)}
									</ThemeProvider>
								</>
							)}
						</ProjectContextProvider>
					)}
				</TenantContextSubscriber>
			</ErrorBoundary>
		);
	}

	render() {
		const { projectKey } = this.props.match.params;
		return (
			<UFOSegment name="people-page">
				<MarkProductStart />
				<ProjectSettingsController projectKey={projectKey}>
					{this.renderContents()}
				</ProjectSettingsController>
			</UFOSegment>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
export default withRouter(PeoplePage) as ComponentType<Record<any, any>>;
